import { useMutation, useQueryClient } from "@tanstack/react-query";

import { variantAttributeKeys } from "./keys";

import { VariantAttribute, UpdateVariantAttributePayload } from "@/types/product";
import { Response } from "@/types/pagination";
import { isMockEnabled, simulateDelay } from "@/mock/config";
import { updateVariantAttributeMock } from "@/mock/handlers/variant-attribute";

import { api } from "@/lib/api-client";

interface UpdateVariantAttributeParams {
  id: string;
  payload: UpdateVariantAttributePayload;
}

export const useUpdateVariantAttribute = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: UpdateVariantAttributeParams) => {
      if (isMockEnabled()) {
        await simulateDelay();
        const result = updateVariantAttributeMock(id, payload);
        if (!result) throw new Error("Variant attribute not found");
        return result;
      }

      const { data } = await api
        .patch(`variant-attributes/${id}`, { json: payload })
        .json<Response<VariantAttribute>>();

      return data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: variantAttributeKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: variantAttributeKeys.detail(id),
      });
    },
  });
};
